import { useSyncExternalStore } from 'react';

// Client-side mirror of a BACKGROUND Telegram send. The send used to run inline
// on the POST, which made the browser the thing keeping ffmpeg alive: a closed
// tab cancelled a transcode seconds from finishing. It now returns a job id
// immediately and reports progress over the "TelegramSend" WS event, so this
// store — not the awaited promise — is what the button reads.
//
// The active job ids are mirrored into localStorage so a reload can re-attach
// through GET /telegram/send-jobs/:id. The connection is disposable now; the
// job id is the durable handle.

export type TelegramSendStatus =
  | 'idle'
  | 'queued'
  | 'compressing'
  | 'uploading'
  | 'sent'
  | 'failed';

export interface TelegramSendState {
  status: TelegramSendStatus;
  jobId: string;
  error: string;
}

export const IDLE_TELEGRAM_SEND: TelegramSendState = { status: 'idle', jobId: '', error: '' };

/** Payload of the backend "TelegramSend" WS event (internal/api/telegram/job.go). */
export interface TelegramSendEvent {
  job_id?: string;
  status?: string;
  clip_id?: string;
  file?: string;
  error?: string;
}

const ACTIVE: TelegramSendStatus[] = ['queued', 'compressing', 'uploading'];

export const isTelegramSendActive = (status: TelegramSendStatus): boolean =>
  ACTIVE.includes(status);

export const normalizeTelegramStatus = (raw: string | undefined): TelegramSendStatus => {
  switch (raw) {
    case 'queued':
    case 'compressing':
    case 'uploading':
    case 'sent':
    case 'failed':
      return raw;
    default:
      return 'idle';
  }
};

/** Human label for the button while the job runs. */
export const telegramSendLabel = (status: TelegramSendStatus): string => {
  switch (status) {
    case 'queued':
      return 'Queued…';
    case 'compressing':
      return 'Compressing…';
    case 'uploading':
      return 'Uploading…';
    default:
      return 'Send to Telegram';
  }
};

// ==================== Module-level store ====================

const STORAGE_KEY = 'telegram_send_jobs';

const store = new Map<string, TelegramSendState>();
const listeners = new Map<string, Set<() => void>>();

const emit = (clipId: string) => {
  listeners.get(clipId)?.forEach((fn) => fn());
};

const setState = (clipId: string, next: TelegramSendState) => {
  store.set(clipId, next);
  persist();
  emit(clipId);
};

/**
 * Persist only the ACTIVE jobs. A finished one has nothing left to re-attach
 * to, and keeping it would resurrect an old toast on the next page load.
 */
const persist = () => {
  try {
    const active: Record<string, string> = {};
    store.forEach((state, clipId) => {
      if (isTelegramSendActive(state.status) && state.jobId) active[clipId] = state.jobId;
    });
    if (Object.keys(active).length === 0) {
      window.localStorage.removeItem(STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(active));
  } catch {
    // A private window or a storage-blocked browser must not break sending.
  }
};

/** clipId → jobId for every send that was still running when the page closed. */
export const readPersistedSendJobs = (): Record<string, string> => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Record<string, string> = {};
    Object.entries(parsed as Record<string, unknown>).forEach(([clipId, jobId]) => {
      if (typeof jobId === 'string' && jobId) out[clipId] = jobId;
    });
    return out;
  } catch {
    return {};
  }
};

export const getTelegramSendState = (clipId: string): TelegramSendState =>
  store.get(clipId) ?? IDLE_TELEGRAM_SEND;

/** Optimistic bridge: the button flips the moment the 202 lands. */
export const markTelegramSendStarted = (clipId: string, jobId: string): void => {
  if (!clipId) return;
  setState(clipId, { status: 'queued', jobId, error: '' });
};

/** Apply a live "TelegramSend" WS event, or a re-attach poll's job snapshot. */
export const applyTelegramSendEvent = (event: TelegramSendEvent): void => {
  const clipId = event.clip_id;
  if (!clipId) return;
  const status = normalizeTelegramStatus(event.status);
  // An event for a job we are no longer tracking must not overwrite a newer
  // send on the same clip: only the job we know about, or the first one seen.
  const current = getTelegramSendState(clipId);
  if (current.jobId && event.job_id && current.jobId !== event.job_id) return;
  setState(clipId, {
    status,
    jobId: event.job_id || current.jobId,
    error: status === 'failed' ? event.error || 'Send failed' : '',
  });
};

/** Drop a clip's entry once the user has seen the outcome. */
export const clearTelegramSendState = (clipId: string): void => {
  if (!store.has(clipId)) return;
  store.delete(clipId);
  persist();
  emit(clipId);
};

/**
 * Re-attach to sends that outlived the page which started them. The job ids
 * were mirrored to localStorage; this asks the backend how each one ended and
 * feeds the answer through the same path a live event takes.
 *
 * A job the backend no longer knows about is dropped rather than left spinning:
 * the registry is in-memory, so a backend restart between the two page loads
 * legitimately means "that send is gone".
 */
export const rehydrateTelegramSendJobs = async (
  fetchJob: (jobId: string) => Promise<TelegramSendEvent>
): Promise<void> => {
  const persisted = readPersistedSendJobs();
  await Promise.all(
    Object.entries(persisted).map(async ([clipId, jobId]) => {
      try {
        const job = await fetchJob(jobId);
        applyTelegramSendEvent({ ...job, clip_id: job.clip_id || clipId, job_id: jobId });
      } catch {
        clearTelegramSendState(clipId);
      }
    })
  );
};

const subscribe = (clipId: string) => (onChange: () => void) => {
  const set = listeners.get(clipId) ?? new Set<() => void>();
  set.add(onChange);
  listeners.set(clipId, set);
  return () => {
    set.delete(onChange);
    if (set.size === 0) listeners.delete(clipId);
  };
};

export function useTelegramSendState(clipId: string): TelegramSendState {
  return useSyncExternalStore(
    subscribe(clipId),
    () => getTelegramSendState(clipId),
    () => IDLE_TELEGRAM_SEND
  );
}
